import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { UserPlusIcon, UserCheckIcon } from "@phosphor-icons/react";

export default function FollowButton({ sellerId, initialFollowing, initialCount, onChange, size = "default" }) {
  const { user } = useAuth();
  const [following, setFollowing] = useState(!!initialFollowing);
  const [count, setCount] = useState(initialCount);
  const [busy, setBusy] = useState(false);
  const [hydrated, setHydrated] = useState(initialFollowing !== undefined);

  // Lazily resolve the current follow state from the server when the
  // parent didn't pass `initialFollowing`. Without this, the button
  // would always render "Follow" even for users who already follow,
  // which is misleading and lets people accidentally re-follow.
  useEffect(() => {
    if (hydrated || !sellerId || !user || user.user_id === sellerId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/sellers/${sellerId}`);
        if (cancelled) return;
        setFollowing(!!data.is_following);
        if (typeof data.follower_count === "number") setCount(data.follower_count);
      } catch {
        /* network — leave default */
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hydrated, sellerId, user]);

  // Hide for self or no seller id
  if (!sellerId || (user && user.user_id === sellerId)) return null;

  const toggle = async () => {
    if (!user) {
      toast.error("Log in to follow sellers.");
      return;
    }
    if (busy) return;
    setBusy(true);
    try {
      const { data } = following
        ? await api.delete(`/sellers/${sellerId}/follow`)
        : await api.post(`/sellers/${sellerId}/follow`);
      setFollowing(data.is_following);
      if (typeof data.follower_count === "number") setCount(data.follower_count);
      onChange?.(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const sizeCls =
    size === "xs"
      ? "h-6 px-2.5 text-[10px] uppercase tracking-wider"
      : size === "sm"
      ? "h-8 px-3 text-xs"
      : size === "lg"
      ? "h-11 px-5 text-sm"
      : "h-9 px-4 text-sm";

  // Tighter icon for the thin XS pill so it visually matches LIVE.
  const iconSize = size === "xs" ? 10 : 14;

  return (
    <Button
      onClick={toggle}
      disabled={busy}
      variant={following ? "outline" : "default"}
      className={`${sizeCls} gap-1.5 transition-colors ${
        following
          ? "border-zinc-300 bg-zinc-100 text-zinc-600 hover:bg-zinc-200 hover:text-zinc-700 hover:border-zinc-300"
          : "bg-[#1D78AB] hover:bg-[#165d85] text-white"
      }`}
      data-testid={following ? "unfollow-seller-btn" : "follow-seller-btn"}
      aria-pressed={following}
    >
      {following ? <UserCheckIcon size={iconSize} weight="fill" /> : <UserPlusIcon size={iconSize} />}
      {following ? "Following" : "Follow"}
      {typeof count === "number" && count > 0 && (
        <span className="ml-1 text-xs opacity-70 tabular-nums">{count}</span>
      )}
    </Button>
  );
}
